import {DeckWorkspace} from './core/deck-workspace';
import type {Route} from './core/routing';
import {HomeView} from './ui/home-view';
import {PresenterView} from './ui/presenter-view';
import {SpeakerView} from './ui/speaker-view';
import {element} from './ui/dom';

async function resolveLegacyPage(deckId: string, legacySlide?: string): Promise<number | undefined> {
  if (!legacySlide) return undefined;
  try {
    const response = await fetch(`/legacy/${encodeURIComponent(deckId)}.json`);
    const aliases = (await response.json()) as {slides: Array<{page: number; id: string}>};
    return aliases.slides.find((entry) => entry.id === legacySlide)?.page;
  } catch {
    return undefined;
  }
}

export async function createApp(route: Route): Promise<HTMLElement> {
  if (route.mode === 'home') return new HomeView().element;
  if (!route.deck) throw new Error('Choose a deck before opening the studio.');
  const workspace = await DeckWorkspace.open(route.deck);
  const legacyPage = await resolveLegacyPage(route.deck, route.legacySlide);
  const page = legacyPage ?? route.page;
  if (route.mode === 'edit') {
    const {EditorView} = await import('./ui/editor-view');
    return new EditorView(workspace, page, route.step).element;
  }
  const assets = await workspace.assets();
  if (route.mode === 'present')
    return new PresenterView(workspace, page, route.step, assets, route.room ?? null).element;
  if (route.mode === 'speaker')
    return new SpeakerView(workspace, page, route.step, assets, route.room ?? null).element;
  throw new Error('Unknown presentation view.');
}

export function errorView(error: unknown): HTMLElement {
  const main = element('main', 'error-page');
  const message = error instanceof Error ? error.message : String(error);
  main.innerHTML = `<div class="brand-mark brand-mark-large">S</div><h1>Could not open this deck.</h1><p></p><a class="button button-primary" href="/">Back home</a>`;
  const paragraph = main.querySelector('p');
  if (paragraph) paragraph.textContent = message;
  return main;
}
